<script setup lang="ts">
/* Types */
import { ICONS } from './types'
import type { IconsKeys, ColorName } from './types'

type Props = {
  name: IconsKeys
  size?: number | string
  color?: string
  isAwait?: boolean
  clickable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 24,
  color: '',
  isAwait: true,
})

const sizeStyle = computed(() => {
  return {
    width: props.size + 'px',
    height: props.size + 'px',
  }
})

const svgStyles = computed(() => {
  const styles: Partial<Record<ColorName, string>> = {}

  if (props.color) {
    styles['--fillColor'] = props.color
    styles['--strokeColor'] = props.color
  }

  return styles
})

// Lazily load the icon
const icon = ref<null | string>(null)

// https://github.com/nuxt/nuxt/issues/14766#issuecomment-1397365205
// https://github.com/nuxt-community/svg-module/issues/95#issuecomment-1344339312
const loadIcon = async () => {
  const iconImport = Object.values(ICONS[props.name])[0]

  if (!iconImport) return

  if (props.isAwait) {
    icon.value = (await iconImport()) as string
  } else {
    // Делаем then, а не await, чтобы не блочить выдачу страницы с сервера
    iconImport().then((value) => (icon.value = value as string))
  }
}

loadIcon()

watch(
  () => props.name,
  () => loadIcon(),
)
</script>

<template>
  <i
    v-if="icon"
    class="app-icon"
    :class="{ 'app-icon--clickable': clickable }"
    :style="{ ...svgStyles, ...sizeStyle }"
    v-bind="$attrs"
    v-html="icon"
  />

  <i v-else class="app-icon" :style="{ ...svgStyles, ...sizeStyle }" />
</template>

<style scoped lang="scss">
.app-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  &--clickable {
    @include media('>=desktop-mini') {
      cursor: pointer;
    }
  }

  :deep(svg) {
    width: 100%;
    height: 100%;
    fill: var(--fillColor, #2c2f39);
    stroke: var(--strokeColor, #2c2f39);
  }
}
</style>
